/**
 * Prefill lander/offer display label from Voluum POST /entity (edit dialog).
 * Tres Commas uses its own resource GET; imported ids never query Voluum.
 * For Media Devoted | Created By Ciro Autiero
 */

import { matchTracker, type TrackerId } from '@trafficpulse/shared';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import { queryTrackerEntity } from '@/features/flow-optimizer/tracker-entities/entity-api';
import { validEntityId } from '@/features/flow-optimizer/tracker-entities/identity';
import { useDrawerProfile, useFlowTracker, useFlowTrackerName } from '@/features/flow-optimizer/tracker';
import { pickVoluumTrackerEntityDisplayName } from '@/features/flow-optimizer/tabs/standard/v2/utils/voluum-tracker-entity-label';

const tcReadsEnabled = (tracker: TrackerId | null) => tracker ? matchTracker(tracker, {
  voluum: () => false, binom: () => false, 'tres-commas': () => true,
}) : false;

export interface UseVoluumTrackerEntityPrefillParams {
  open: boolean;
  mode: 'add' | 'edit';
  entityLabel: 'lander' | 'offer';
  entityId: string;
  setDisplayName: Dispatch<SetStateAction<string>>;
}

export function useVoluumTrackerEntityPrefill(
  params: UseVoluumTrackerEntityPrefillParams,
): { loading: boolean; error: string | null } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tracker = useFlowTracker();
  const trackerName = useFlowTrackerName();
  const enabled = useDrawerProfile().voluumEntityApis || tcReadsEnabled(tracker);

  useEffect(() => {
    if (!enabled || !params.open || params.mode !== 'edit') {
      setLoading(false);
      setError(null);
      return;
    }

    const id = params.entityId.trim();
    if (!validEntityId(tracker, params.entityLabel, id)) {
      setLoading(false);
      setError(null);
      return;
    }

    const kind = params.entityLabel === 'lander' ? 'LANDER' : 'OFFER';
    let cancelled = false;
    setLoading(true);
    setError(null);

    void queryTrackerEntity(tracker, { type: kind, id })
      .then((data) => {
        if (cancelled) return;
        const suggested = pickVoluumTrackerEntityDisplayName(data);
        if (!suggested) return;
        params.setDisplayName((prev) =>
          prev.trim() !== '' ? prev : suggested,
        );
      })
      .catch(() => {
        if (cancelled) return;
        setError(`Could not load this entity from ${trackerName}.`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled, tracker, trackerName,
    params.open,
    params.mode,
    params.entityLabel,
    params.entityId,
    params.setDisplayName,
  ]);

  return { loading, error };
}
